import { useEffect, useMemo, useState } from "react";
import { TbFileInvoice } from "react-icons/tb";
import { RiDeleteBin5Line } from "react-icons/ri";
import ModalElement from "../../../Common/ModalElement"
import InputElement from "../../../Common/InputElement";
import ButtonElement from "../../../Common/ButtonElement";
import SearchableDropdown from "../../../Common/SearchDropdown";
import CSVUploader from "../../../Common/Uploader";
import { validateFields, showToast, commonOnChange, commonOnSelect, isValidObject, formatListItems } from "../../../utils";
import TableElement from "../../../Common/TableElement";
import AsyncInputField from "../../../Common/AsyncInputElement";
import { imeiAutoComplete } from "../../../actions/inventory";
import { appApis, csvUrls, defaultCSVHeaders, headerColumn } from "../../../constants";
import { useApiRequest } from "../../../hooks/useApiRequest";
import CustomToolTip from "../../../Common/CustomTooltip";


const Transfer = ({ show, refreshData, hideModal, data, companies, installers, wareHouseList, suppliers }) => {
    const [transferDetails, setTransferDetails] = useState({
        type: "",
        typeId: "",
        status: "",
        statusId: "",
        recipient: "",
        recipientId: "",
        inventoryType: "",
        inventoryTypeId: "",
        stateList: [],
        recipientList: [],
        typeErr: false,
        statusErr: false,
        recipientErr: false,
        inventoryTypeErr: false,
    });
    const [courierDetails, setCourierDetails] = useState({
        name: "",
        tracking: "",
        dispatchDate: "",
        contact: "",
        remark: "",
        nameErr: false,
        remarkErr: false,
        contactErr: false,
        trackingErr: false,
        dispatchDateErr: false
    });

    const [imeiDetails, setImeiDetails] = useState({
        value: "",
        imeiList: [],
        valueErr: false
    })

    const { request: saveTransfer, loading: saveLoading } = useApiRequest();

    const { type, status, recipient, inventoryType, inventoryTypeId, statusId, typeId, stateList, recipientId, typeErr, statusErr, recipientErr, inventoryTypeErr, recipientList } = transferDetails;
    const { name, tracking, dispatchDate, contact, remark, nameErr, remarkErr, contactErr, trackingErr, dispatchDateErr } = courierDetails;

    const { value, imeiList, valueErr } = imeiDetails;
    const invType = inventoryTypeId == 20 ? 2 : inventoryTypeId == 21 ? 3 : 1;
    const isCourier = typeId == 26;

    const onChangeHandler = (event) => commonOnChange(event, setCourierDetails);
    const onSelectHandler = (event) => {
        const { name, value } = event?.target ?? {};
        if (name == "inventoryType") {
            setImeiDetails(prev => ({ ...prev, value: "", valueErr: false }))
        } else if (name == "type") {
            const list = formatListItems(value?.allowed_states, "inventory_state_id", "inventory_state");
            let recipients = installers;
            if (value?.id == 26) recipients = [...recipients, ...companies]
            else if (value?.id == 29) recipients = companies;
            setTransferDetails(prev => ({ ...prev, stateList: list, recipientList: recipients, status: "", statusId: "", recipient: "", recipientId: "" }))
        } else if (name == "status") {
            const key = value?.id == 9 ? "installerId" : value?.id == 16 ? "supplierId" : value?.id == 10 ? "warehouseId" : "companyId";
            const totalList = [...installers, ...companies, ...suppliers, ...wareHouseList];
            const list = totalList.filter(item => item.hasOwnProperty(key));
            setTransferDetails(prev => ({ ...prev, recipientList: list, recipient: "", recipientId: "" }))
        }
        commonOnSelect(event, setTransferDetails)
    };

    const updateItemsByCsv = (data) => {
        let temp = [];
        if (data) temp = data;
        setImeiDetails(prev => ({ ...prev, imeiList: [...temp] }));
    }

    const removeIMEI = (imeiIndex) => {
        const temp = imeiList.filter((item, index) => index != imeiIndex);
        setImeiDetails(prev => ({ ...prev, imeiList: [...temp] }));
    }

    const fetchSuggestions = async (query) => {
        if (!inventoryType) {
            showToast("Please select Inventory Type!!!", "error")
            return [];
        }
        const response = await imeiAutoComplete(query, inventoryType == "SIM" ? "SIM" : inventoryType == "GPS" ? "GPS" : "GWS", typeId == 39 ? "Installer" : typeId == 41 ? "Supplier" : "Warehouse");
        const results = response?.data?.data?.results || [];
        return results; // Adjust based on API response structure
    };

    const handleInputChange = (e) => {
        setImeiDetails(prev => ({ ...prev, value: e.target.value, valueErr: null }));
    };

    const onHandleSelect = (data) => {
        if (data) addImei({
            gpsImeiNo: data?.gpsImei,
            deviceModal: data?.deviceModel,
            simImeiNo: data?.iccid,
            simNum: data?.simNumber
        });
    }

    const addImei = (tableData) => {
        if (isValidObject(tableData)) {
            const temp = [...imeiList, { ...tableData }];
            setImeiDetails(prev => ({ ...prev, imeiList: [...temp], value: "", valueErr: null }));
        }
    }

    const getPostObj = () => {
        return {
            inventory_type_id: inventoryTypeId,
            transfer_type_id: typeId,
            inventory_state_id: statusId,
            recipient_id: recipientId,
            courier_details: isCourier ? {
                courier_name: name,
                tracking_number: tracking,
                dispatch_date: dispatchDate,
                contact_info: contact,
                remark,
            } : null,
            item_ids: imeiList.map(item => invType == 3 ? item.simImeiNo : item.gpsImeiNo)
        }
    }

    const submit = () => {
        const checkField = stateList.length ? { statusErr: [status, "text"] } : {}
        validateFields(
            {
                inventoryTypeErr: [inventoryType, "text"],
                typeErr: [type, "text"],
                ...checkField,
                recipientErr: [recipient, "text"]
            },
            setTransferDetails,
            () => {
                const validateAndSubmit = () => {
                    if (imeiList.length === 0) {
                        showToast("Please Enter IMEI details to submit!!!", "error");
                    } else {
                        const payload = getPostObj();
                        saveTransfer({
                            method: "POST",
                            url: appApis.inventoryTransfer,
                            body: payload,
                            onSuccess: () => {
                                refreshData();
                                hideModal();
                            }
                        })
                    }
                };

                if (isCourier) {
                    validateFields(
                        {
                            nameErr: [name, "text"],
                            trackingErr: [tracking, "text"],
                            dispatchDateErr: [dispatchDate, "date"],
                            contactErr: [contact, "phone"]
                        },
                        setCourierDetails,
                        validateAndSubmit
                    );
                } else {
                    validateAndSubmit();
                }
            }
        );
    }

    const isUpdate = useMemo(() => data?.selectedInv?.inventoryTypeId, [data])

    useEffect(() => {
        if (isValidObject(data?.selectedInv)) {
            const defaultData = data?.selectedInv ?? {};
            setTransferDetails(prev => ({ ...prev, inventoryType: defaultData?.inventoryType ?? "", inventoryTypeId: defaultData?.inventoryTypeId ?? "", status: defaultData?.inventoryState, statusId: defaultData?.inventoryStateId }));
            setImeiDetails(prev => ({ ...prev, imeiList: [{ gpsImeiNo: defaultData?.gpsImei, deviceModal: defaultData?.deviceModel, simImeiNo: defaultData?.iccid, simNum: defaultData?.simNumber }] }))
        }
    }, [data?.selectedInv])

    return <ModalElement FooterComponent={<div className="submit-btn text-center my-2">
        <ButtonElement disabled={imeiList.length == 0} loading={saveLoading} text={"Submit"} cssClass="small-radius" onClick={submit} variant="btn-outline-primary" />
    </div>} size="xl" show={show} title={"Inventory  Transfer"} onHide={hideModal}>
        <div className="add-inventory">
            <div className="transaction-section mb-3">
                <h5 className="mb-3">Transfer Details</h5>
                <div className="row w-100">
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            disabled={data?.selectedInv?.inventoryType}
                            label={"Inventory Type"}
                            placeholder={"Select Inventory Type"}
                            value={inventoryType}
                            error={inventoryTypeErr}
                            options={data.inventoryList}
                            onSelect={(value) => onSelectHandler({ target: { name: "inventoryType", value } })}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Transfer Type"}
                            placeholder={"Select Transfer Type"}
                            value={type}
                            error={typeErr}
                            options={data.transferList}
                            onSelect={(value) => onSelectHandler({ target: { name: "type", value } })}
                        />
                    </div>
                    {stateList?.length ? <div className="col-lg-4 col-md-6">
                        <SearchableDropdown
                            label={"State"}
                            placeholder={"Select State"}
                            value={status}
                            error={statusErr}
                            options={stateList}
                            onSelect={(value) => onSelectHandler({ target: { name: "status", value } })}
                        />
                    </div> : null}
                    {recipientList?.length ? <div className="col-lg-4 col-md-6">
                        <SearchableDropdown
                            label={"Recipient"}
                            placeholder={"Select Recipient"}
                            value={recipient}
                            error={recipientErr}
                            options={recipientList}
                            onSelect={(value) => onSelectHandler({ target: { name: "recipient", value } })}
                        />
                    </div> : null}
                </div>
            </div>

            {isCourier ? <div className="inventory-section my-3">
                <h5 className="mb-3">Courier Details</h5>
                <div className="row w-100">
                    <div className={`col-lg-4 col-md-6 mb-2`}>
                        <InputElement
                            label={"Courier Name"}
                            placeholder={"Enter Courier Name"}
                            name={"name"}
                            value={name}
                            error={nameErr}
                            icon={<TbFileInvoice size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className={`col-lg-4 col-md-6 mb-2`}>
                        <InputElement
                            label={"Tracking /AWB no"}
                            placeholder={"Enter Tracking /AWB no"}
                            name={"tracking"}
                            value={tracking}
                            error={trackingErr}
                            icon={<TbFileInvoice size={20} />}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className={`col-lg-4 col-md-6 mb-2`}>
                        <InputElement
                            label={"Dispatch Date"}
                            placeholder={"Enter Dispatch Date"}
                            name={"dispatchDate"}
                            type="date"
                            value={dispatchDate}
                            error={dispatchDateErr}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className={`col-md-6 mb-2`}>
                        <InputElement
                            label={"Contact Details"}
                            placeholder={"Enter Contact Details"}
                            name={"contact"}
                            value={contact}
                            error={contactErr}
                            onChange={onChangeHandler}
                        />
                    </div>
                    <div className={`col-md-6 mb-2`}>
                        <InputElement
                            label={"Remark"}
                            placeholder={"Enter Remark"}
                            name={"remark"}
                            value={remark}
                            error={remarkErr}
                            onChange={onChangeHandler}
                        />
                    </div>
                </div>
            </div> : null}
            <div className="transfer-imei-details my-4 me-4">
                <h5 className="mb-3">IMEI Details</h5>
                <div className="mb-3">
                    <div className="imei-search">
                        <div className="w-100 me-2">
                            <AsyncInputField
                                isRequired={false}
                                disabled={isUpdate}
                                key={inventoryType}
                                previewKey={"SearchItem"}
                                value={value}
                                fetchSuggestions={fetchSuggestions}
                                name="value"
                                label={
                                    <>
                                        {`${invType != 3 ? "GPS " : ""}IMEI number`}
                                        <span className="ms-1">
                                            <CustomToolTip content={"It will only search items that are not in use!!!"} />
                                        </span>
                                    </>}
                                onChange={handleInputChange}
                                onSelect={onHandleSelect}
                                placeholder={`Enter ${invType != 3 ? "GPS " : ""}IMEI number`}
                                error={valueErr}
                                setError={(err) => setImeiDetails(prev => ({ ...prev, valueErr: err }))}
                            />
                        </div>
                        {/* <div className="search-btn text-center">
                            <ButtonElement text={<FaPlus size={16} />} cssClass="small-radius plus-btn" onClick={addImei} variant="btn-outline-primary" />
                        </div> */}
                    </div>

                    <div className="divider">
                        <span>OR</span>
                    </div>

                    <div className="uploader">
                        <CSVUploader disable={isUpdate} acceptType=".csv" icon={<TbFileInvoice size={20} />} expectedHeaders={defaultCSVHeaders.transfer[invType]} setData={updateItemsByCsv} />
                        <a href={csvUrls.transfer[invType]} download className="action-text-left d-flex justify-content-end mt-2"> Download Sample CSV </a>
                    </div>
                </div>
                {imeiList.length > 0 ? <div className="device-log-table mb-3">
                    <TableElement data={imeiList} columns={headerColumn.transfer[invType]} render={(tdData, index) => <tr key={index}>
                        <td>{(index ?? 0) + 1}</td>
                        {invType != 3 ? <td>{tdData?.gpsImeiNo || "-"}</td> : null}
                        {invType != 3 ? <td>{tdData?.deviceModal || "-"}</td> : null}
                        {invType != 2 ? <td>{tdData?.simImeiNo || "-"}</td> : null}
                        {invType != 2 ? <td>{tdData?.simNum || "-"}</td> : null}
                        <td>
                            <ButtonElement text={<RiDeleteBin5Line size={20} />} cssClass="small-radius small-btn" onClick={() => removeIMEI(index)} variant="btn-delete-icon" loading={false} />
                        </td>
                    </tr>} />
                </div> : null}
            </div>
        </div>
    </ModalElement>
}

export default Transfer;